import { ACCESS_RIGHTS, GENDER, COMPANY_TYPES, PERMISSIONS } from '../resources/enums';
import { IBranch, IPolicyDocument } from '../resources/interfaces';
import { Company } from './company/Company';
import { ICompany } from './company/ICompany';
import { IUserAccount } from './UserAccount';


export class User {
  id: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  email: string;
  profileImage?: string;
  jobTitle?: string;
  username?: string;
  gender?: GENDER | undefined;
  phone?: string;
  city?: string;
  country?: ICountry | string;
  accessRight?: ACCESS_RIGHTS | undefined;
  account?: IUserAccount;
  image?: string;
  teamName?: string;
  company: Company | undefined;
  documents?: IPolicyDocument[];
  role?: IRole;
  branch?: IBranch;

  constructor(IUserModel: IUser) {
    this.id = IUserModel.id;
    this.firstName = IUserModel.firstName ?? undefined;
    this.lastName = IUserModel.lastName ?? undefined;
    this.fullName = IUserModel.fullName ?? `${this.firstName} ${this.lastName}`;
    this.email = IUserModel.email ?? undefined;
    this.profileImage = IUserModel.profileImage ?? undefined;
    this.jobTitle = IUserModel.jobTitle ?? undefined;
    this.username = IUserModel.username ?? undefined;
    this.gender = IUserModel.gender;
    this.phone = IUserModel.phone ?? undefined;
    this.city = IUserModel.city ?? undefined;
    this.country = typeof IUserModel.country === 'string' ? IUserModel.country : IUserModel.country?.name;
    this.accessRight = IUserModel?.accessRight;
    this.teamName = IUserModel.teamName;
    this.company = IUserModel.company ? new Company(IUserModel.company) : undefined;
    this.account = IUserModel.account ?? null;
    this.role = IUserModel.role ?? undefined;
    this.image = IUserModel.image;
    this.branch = IUserModel.branch;
  }

  private _permissionSet?: Set<string>;

  private get permissionSet(): Set<string> {
    if (!this._permissionSet) {
      this._permissionSet = new Set(
        this.role?.scopes?.map((scope: IScope) => scope.key) ?? []
      );
    }
    return this._permissionSet;
  }

  hasPermission(permission: keyof typeof PERMISSIONS): boolean {
    return this.permissionSet.has(permission);
  }

  canCreateRNPL(): boolean {
    return false;
    // return this.hasPermission(PERMISSIONS.CAN_CREATE_RNPL);
  }

  canUpdatePolicies(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_UPDATE_POLICIES);
  }

  canViewLeads(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_LEADS);
  }

  canViewQuotes(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_QUOTES);
  }

  canApproveTeamPayouts(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_APPROVE_TEAM_PAYOUTS);
  }

  canViewDashboardSalesChannelDonut(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_DASHBOARD_SALES_CHANNEL_DONUT);
  }

  canViewDashboardAccountMetrics(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_DASHBOARD_ACCOUNT_METRICS);
  }

  canInviteTeamUsers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_INVITE_TEAM_USERS);
  }

  canCreateQuotes(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_CREATE_QUOTES);
  }

  canViewTransaction(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_TRANSACTION);
  }

  canViewTeamPayoutDetails(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_TEAM_PAYOUT_DETAILS);
  }

  canEditSettlementAccount(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_SETTLEMENT_ACCOUNT);
  }

  canCreateTeams(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_CREATE_TEAMS);
  }

  canViewTeamSettings(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_TEAM_SETTINGS);
  }

  canAddFuelTransaction(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_ADD_FUEL_TRANSACTION);
  }

  canCreateTransaction(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_CREATE_TRANSACTION);
  }

  canViewTeams(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_TEAMS);
  }

  canViewPolicyPayments(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_POLICY_PAYMENTS);
  }

  canCancelPolicies(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_CANCEL_POLICIES);
  }

  canReassignPolicies(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_REASSIGN_POLICIES);
  }

  canViewPolicyOwnerDocuments(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_POLICY_OWNER_DOCUMENTS);
  }

  canViewNotificationSettings(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_NOTIFICATION_SETTINGS);
  }

  canViewPolicyVehiclePhotos(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_POLICY_VEHICLE_PHOTOS);
  }

  canDeleteVouchers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_DELETE_VOUCHERS);
  }

  canDeleteRolesPermissions(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_DELETE_ROLES_PERMISSIONS);
  }

  canViewBusinessSettings(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_BUSINESS_SETTINGS);
  }

  canAddFleetVehicle(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_ADD_FLEET_VEHICLE);
  }

  canViewDeveloperSettings(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_DEVELOPER_SETTINGS);
  }

  canDeleteTransaction(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_DELETE_TRANSACTION);
  }

  canEditClaims(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_CLAIMS);
  }

  canCreateVehicles(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_CREATE_VEHICLES);
  }

  canOnboardRideSharingDriver(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_ONBOARD_RIDE_SHARING_DRIVER);
  }

  canCreateCommunity(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_CREATE_COMMUNITY);
  }

  canViewRideSharingDrivers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_RIDE_SHARING_DRIVERS);
  }

  canViewTeamPayout(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_TEAM_PAYOUT);
  }

  canViewPayments(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_PAYMENTS);
  }

  canViewPolicyVehicleDocuments(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_POLICY_VEHICLE_DOCUMENTS);
  }

  canViewSettlementAccount(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_SETTLEMENT_ACCOUNT);
  }

  canViewDashboardFinancials(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_DASHBOARD_FINANCIALS);
  }

  canEditTransaction(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_TRANSACTION);
  }

  canEditDeveloperSettings(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_DEVELOPER_SETTINGS);
  }

  canViewRideSharingAnalytics(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_RIDE_SHARING_ANALYTICS);
  }

  canViewCustomerPolicy(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_CUSTOMER_POLICY);
  }

  canEditInsurers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_INSURERS);
  }

  canViewVouchers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_VOUCHERS);
  }

  canSetTeamRelationshipManager(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_SET_TEAM_RELATIONSHIP_MANAGER);
  }

  canViewFleetReports(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_FLEET_REPORTS);
  }

  canEditTeamSettings(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_TEAM_SETTINGS);
  }

  canViewPayouts(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_PAYOUTS);
  }

  canEditLeads(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_LEADS);
  }

  canViewCustomers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_CUSTOMERS);
  }

  canCreateRolesPermissions(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_CREATE_ROLES_PERMISSIONS);
  }

  canViewDashboardMarketingMetrics(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_DASHBOARD_MARKETING_METRICS);
  }

  canViewFleet(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_FLEET);
  }

  canGenerateTeamPayout(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_GENERATE_TEAM_PAYOUT);
  }

  canEditPolicies(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_POLICIES);
  }

  canEditNotificationSettings(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_NOTIFICATION_SETTINGS);
  }

  canViewFuelReports(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_FUEL_REPORTS);
  }

  canManageFuelVendors(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_MANAGE_FUEL_VENDORS);
  }

  canViewTeamMemberDetails(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_TEAM_MEMBER_DETAILS);
  }

  canActivateTeamMembers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_ACTIVATE_TEAM_MEMBERS);
  }

  canRemoveFleetVehicle(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_REMOVE_FLEET_VEHICLE);
  }

  canRemoveTeamMember(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_REMOVE_TEAM_MEMBER);
  }

  canEditVouchers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_VOUCHERS);
  }

  canViewTeamCommissions(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_TEAM_COMMISSIONS);
  }

  canViewInsurers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_INSURERS);
  }

  canEditSecuritySettings(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_SECURITY_SETTINGS);
  }

  canChangeTeamMemberAccess(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_CHANGE_TEAM_MEMBER_ACCESS);
  }

  canViewTeamMembers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_TEAM_MEMBERS);
  }

  canViewDashboardSalesMixDonut(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_DASHBOARD_SALES_MIX_DONUT);
  }

  canViewTeamPerformance(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_TEAM_PERFORMANCE);
  }

  canEditSales(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_SALES);
  }

  canViewDashboardLeadDonut(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_DASHBOARD_LEAD_DONUT);
  }

  canEditRolesPermissions(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_ROLES_PERMISSIONS);
  }

  canReviewPolicies(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_REVIEW_POLICIES);
  }

  canCreateInsurers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_CREATE_INSURERS);
  }

  canSetTeamCommissionRate(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_SET_TEAM_COMMISSION_RATE);
  }

  canInviteTeamMembers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_INVITE_TEAM_MEMBERS);
  }

  canApprovePolicies(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_APPROVE_POLICIES);
  }

  canViewProfileSettings(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_PROFILE_SETTINGS);
  }

  canViewRolesPermissions(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_ROLES_PERMISSIONS);
  }

  canViewPolicies(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_POLICIES);
  }

  canEditCommunity(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_COMMUNITY);
  }

  canViewVehicles(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_VEHICLES);
  }

  canViewCommunity(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_COMMUNITY);
  }

  canManageFleetDrivers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_MANAGE_FLEET_DRIVERS);
  }

  canViewFuelTransactions(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_FUEL_TRANSACTIONS);
  }

  canViewClaims(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_CLAIMS);
  }

  canReviewTeamPayouts(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_REVIEW_TEAM_PAYOUTS);
  }

  canCreateVouchers(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_CREATE_VOUCHERS);
  }

  canEditBusinessSettings(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_BUSINESS_SETTINGS);
  }

  canViewDashboardSalesAmountDonut(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_DASHBOARD_SALES_AMOUNT_DONUT);
  }

  canViewDashboardPolicySales(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_DASHBOARD_POLICY_SALES);
  }

  canEditTeams(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_EDIT_TEAMS);
  }

  canAddTeamMember(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_ADD_TEAM_MEMBER);
  }

  canViewSales(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_VIEW_SALES);
  }

  canCreateLeads(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_CREATE_LEADS);
  }

  canPerformPolicyActions(): boolean {
    return this.hasPermission(PERMISSIONS.CAN_PERFORM_POLICY_ACTIONS);
  }

  isAdministrator(): boolean {
    return this.accessRight === ACCESS_RIGHTS.ADMINISTRATOR
  }

  isAgencyAdmin(): boolean {
    return this.accessRight === ACCESS_RIGHTS.ADMINISTRATOR && this.company?.type === COMPANY_TYPES.AGENCY;
  }

  isInsurerAdmin(): boolean {
    return this.accessRight === ACCESS_RIGHTS.ADMINISTRATOR && this.company?.type === COMPANY_TYPES.INSURER;
  }

  isFuelVendor(): boolean {
    return this.accessRight === ACCESS_RIGHTS.AGENT && this.company?.type === COMPANY_TYPES.FUEL_PROVIDER;
  }

  isFuelAdmin(): boolean {
    return this.accessRight === ACCESS_RIGHTS.ADMINISTRATOR && this.company?.type === COMPANY_TYPES.FUEL_PROVIDER;
  }

  isOwner(): boolean {
    return this.accessRight === ACCESS_RIGHTS.OWNER
  }

  isInternalOwner(): boolean {
    return this.accessRight === ACCESS_RIGHTS.OWNER && this.company?.type === COMPANY_TYPES.INTERNAL;
  }

  isInsurerOwner(): boolean {
    return this.accessRight === ACCESS_RIGHTS.OWNER && this.company?.type === COMPANY_TYPES.INSURER;
  }

  isAgent(): boolean {
    return this.accessRight === ACCESS_RIGHTS.AGENT;
  }

  isInsurerAgent(): boolean {
    return this.accessRight === ACCESS_RIGHTS.AGENT && this.company?.type === COMPANY_TYPES.INSURER;
  }

  isManager(): boolean {
    return this.accessRight === ACCESS_RIGHTS.MANAGER;
  }

  isFuelManager(): boolean {
    return this.accessRight === ACCESS_RIGHTS.MANAGER && this.company?.type === COMPANY_TYPES.FUEL_PROVIDER;
  }

  isSupervisor(): boolean {
    return this.accessRight === ACCESS_RIGHTS.SUPERVISOR;
  }

  isInternalSupervisor(): boolean {
    return this.accessRight === ACCESS_RIGHTS.SUPERVISOR && this.company?.type === COMPANY_TYPES.INTERNAL;
  }

   isInsurerSupervisor(): boolean {
    return this.accessRight === ACCESS_RIGHTS.SUPERVISOR && this.company?.type === COMPANY_TYPES.INSURER;
  }

  isFinancePartner(): boolean {
    return this.company?.type === COMPANY_TYPES.FINANCIER;
  }


  isExternalSuper(): boolean {
    return (
      this.company?.type !== COMPANY_TYPES.INTERNAL &&
      this.accessRight === ACCESS_RIGHTS.OWNER ||
      this.accessRight === ACCESS_RIGHTS.SUPERVISOR
    );
  }

  isInternalAdmin(): boolean {
    return this.company?.type === COMPANY_TYPES.INTERNAL;
  }

  isInsurer(): boolean {
    return this.company?.type === COMPANY_TYPES.INSURER;
  }

  isBroker(): boolean {
    return this.company?.type === COMPANY_TYPES.BROKER;
  }

  isBrokerOwner(): boolean {
    return this.accessRight === ACCESS_RIGHTS.OWNER && this.company?.type === COMPANY_TYPES.BROKER;
  }

  isRideSharing(): boolean {
    return this.company?.type === COMPANY_TYPES.RIDE_SHARING;
  }

  isPartner(): boolean {
    return (
      this.company?.type !== COMPANY_TYPES.INTERNAL
    );
  }

  isFinancier(): boolean {
    return this.company?.type === COMPANY_TYPES.FINANCIER;
  }

  isFuelProvider(): boolean {
    return this.company?.type === COMPANY_TYPES.FUEL_PROVIDER;
  }

  isAgency(): boolean {
    return this.company?.type === COMPANY_TYPES.AGENCY;
  }

  isFleet(): boolean {
    return this.company?.type === COMPANY_TYPES.FLEET;
  }

  isInGhana(): boolean {
    return typeof this.country === 'string' ? this.country === 'GH' : this.country?.iso === 'GH';
  }

  isInNigeria(): boolean {
    return typeof this.country === 'string' ? this.country === 'NG' : this.country?.iso === 'NG';
  }
}
export interface IUser {
  id: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  email: string;
  jobTitle?: string;
  username?: string;
  gender?: GENDER;
  phone?: string;
  city?: string;
  accessRight?: ACCESS_RIGHTS;
  teamName?: string;
  company?: ICompany;
  referralCode?: string;
  account?: any;
  country?: ICountry | string;
  lastSeen?: string;
  verified?: boolean;
  profileImage?: string;
  role?: IRole;
  image?: string;
  branch?: IBranch;
}

export interface ICountry {
  name: string;
  code: string;
  iso: string;
  language: string;
  currencySymbol: string;
  currencyIso: string;
  flag: string;
}

export interface IRole {
  id: number;
  name: string;
  description: string;
  type: string;
  accessRight: string;
  refCodePrefix: string;
  scopes: IScope[];
  active: boolean;
  createdBy: string;
  createdOn: string;
  updatedOn: string;
}

export interface IScope {
  key: string;
  title: string;
  description: string;
  category: string;
}
